import React from 'react';
import { Card, CardBody, Table } from 'reactstrap';

import Loading from './Loading';
import Pagination from './Pagination';

import './DataTable.css';

/**
 * The standard table layout, taken from the Orders page.
 *
 * List pages render the default carded form. Tables embedded in a detail-page
 * tab are already inside a card, so they pass `card={false}` and get the
 * styling, the scroll wrapper and the pagination strip without a second card
 * drawn around them.
 *
 *   <DataTable minWidth={700} loading={loading} page={page}
 *              total={meta.pages} onPageChange={this.setPage}
 *              showPagination={rows.length > 0}>
 *     <thead className="text-primary"><tr><th>…</th></tr></thead>
 *     <tbody>…</tbody>
 *   </DataTable>
 */
export default function DataTable({
  children,
  card = true,
  minWidth,
  loading = false,
  page = 1,
  total,
  onPageChange,
  showPagination,
  footer,
  className = '',
  style,
}) {
  // Pagination is opt-in via `onPageChange`; `showPagination` lets a caller
  // hide the strip when there is nothing to page through.
  const paginate =
    Boolean(onPageChange) && (showPagination === undefined || showPagination);

  const body = (
    <React.Fragment>
      <div className="bp-table-scroll">
        <Table
          hover
          striped
          className={'bp-table ' + className}
          style={{ minWidth, ...style }}
        >
          {children}
        </Table>
      </div>

      {paginate && (
        <div className="bp-table-footer">
          <Pagination page={page} total={total} onChange={onPageChange} />
        </div>
      )}

      {/* Tables that page by "load more" rather than page numbers put their
          control here so it lands in the same strip. */}
      {footer && <div className="bp-table-footer">{footer}</div>}

      {loading && <Loading />}
    </React.Fragment>
  );

  if (!card) return body;

  return (
    <Card>
      <CardBody style={{ padding: 0 }}>{body}</CardBody>
    </Card>
  );
}

/**
 * The standard page header: title, grey sub-line, and any actions (search,
 * filters, buttons) pushed to the right.
 */
export function PageHeader({ title, subtitle, children, beforeTitle }) {
  return (
    <div className="bp-page-header">
      <div className="bp-page-header__titles">
        {beforeTitle}
        <h4 className="bp-page-header__title">{title}</h4>
        {subtitle !== undefined && subtitle !== null && (
          <p className="bp-page-header__sub">{subtitle}</p>
        )}
      </div>
      {children && <div className="bp-page-header__actions">{children}</div>}
    </div>
  );
}

/**
 * The "nothing here" body. Rendered in place of <tbody> so the column headers
 * stay visible above it.
 */
export function EmptyState({
  colSpan = 10,
  heading = 'No results',
  message,
  actionLabel,
  onAction,
  icon = true,
}) {
  return (
    <tbody>
      <tr>
        <td colSpan={colSpan} style={{ border: 'none' }}>
          <div className="bp-empty">
            {icon && (
              <div className="bp-empty__icon">
                <svg
                  width="40"
                  height="40"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="#dddddd"
                  strokeWidth="1.4"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <circle cx="11" cy="11" r="8" />
                  <line x1="21" y1="21" x2="16.65" y2="16.65" />
                  <line x1="8" y1="11" x2="14" y2="11" />
                </svg>
              </div>
            )}
            <p className="bp-empty__heading">{heading}</p>
            {message && <p className="bp-empty__sub">{message}</p>}
            {actionLabel && onAction && (
              <button className="bp-empty__action" onClick={onAction}>
                {actionLabel}
              </button>
            )}
          </div>
        </td>
      </tr>
    </tbody>
  );
}
